// src/features/system/developer/components/simulations/mercadopago/api/mercadopagoApi.ts
// ────────────────────────────────────────────────────────────────────────────
// MercadoPago frontend API layer — typed client for the BFF /mercadopago/*.
//
// Uses the generic `bff` client (respects session, CSRF, error handling).
// Endpoint paths match server/bff/modules/services/mercadopago/routes.mjs.
// ────────────────────────────────────────────────────────────────────────────

import { bff } from '@/lib/http/bff';

/* ─── Types ─────────────────────────────────────────────────────────── */

export interface Tenant {
  id: string;
  name: string;
  slug: string;
  status: 'pending' | 'active' | 'suspended' | 'disconnected';
  owner_name?: string;
  email?: string;
  phone?: string;
  is_mp_connected: boolean;
  connect_url?: string;
}

export interface CreateTenantInput {
  name: string;
  slug: string;
  owner_name?: string;
  email?: string;
  phone?: string;
}

export interface AuthStatus {
  connected: boolean;
  /** True when orchestrator is reachable (even if no credentials stored yet) */
  orchestrator_ready?: boolean;
  mp_user_id?: number | null;
  store_id?: string | null;
  pos_id?: string | null;
  token_expires_at?: string | null;
  message?: string;
  connect_url?: string | null;
  // Dev-mode fields returned when orchestrator is unreachable
  dev_mode?: boolean;
  service_unavailable?: boolean;
}

export interface ConnectTenantInput {
  mp_user_id?: number;
  access_token?: string;
}

export interface CreateOrderInput {
  table_number: string;
  amount: number;
  tenant_id?: string;
  items?: OrderItem[];
}

export interface OrderItem {
  sku_number: string;
  title: string;
  description: string;
  category: string;
  quantity: number;
  unit_price: number;
  unit_measure: string;
  total_amount: number;
}

export interface QRPaymentResponse {
  order_id: string;
  order: Record<string, unknown>;
  payment_id: string | null;
  qr_data: string;
  qr_image_url: string;
  message: string;
  // Orchestrator fields
  workflow_run_id?: string;
  run_id?: string;
  status?: string;
  dev_mode?: boolean;
}

export interface StepOutcome {
  /** PENDING | READY | RUNNING | SUCCEEDED | FAILED */
  status: string;
  handler_key: string;
  outputs: Record<string, unknown>;
  error: string;
  attempts: number;
  started_at?: string | null;
  finished_at?: string | null;
  duration_ms?: number | null;
}

export interface StepError {
  step: string;
  error: string;
  attempts: number;
}

export interface OrderStatus {
  order_id: string;
  /** Derived status: started | pending | running | running:<step> | paid | failed | unknown */
  status: string;
  /** Raw orchestrator run status: PENDING | RUNNING | SUCCEEDED | FAILED */
  run_status?: string;
  total_amount: number;
  table_number: string;
  payments?: PaymentInfo[];
  /** Per-step status map keyed by step_id */
  steps?: Record<string, StepOutcome>;
  qr_data?: string;
  qr_image_url?: string;
  /** Non-empty when any step failed; each entry has step, error, attempts */
  errors?: StepError[];
  /** Human-readable error summary joining all step errors */
  error_summary?: string;
  dev_mode?: boolean;
  items?: OrderItem[];
  created_at?: string;
  updated_at?: string;
}

export interface PaymentInfo {
  id: string;
  status: 'pending' | 'approved' | 'rejected' | 'cancelled' | 'refunded' | 'in_process';
  amount: number;
  method: string;
  mp_payment_id?: string;
  created_at: string;
}

/* ─── API calls ─────────────────────────────────────────────────────── */

const PREFIX = '/mercadopago';

/** Register a new tenant (retailer) */
export async function createTenant(input: CreateTenantInput): Promise<Tenant> {
  return bff.post<Tenant>(`${PREFIX}/tenants`, input);
}

/** List all tenants */
export async function listTenants(): Promise<Tenant[]> {
  return bff.get<Tenant[]>(`${PREFIX}/tenants`);
}

/** Get the MP OAuth authorization URL for a tenant */
export async function getAuthUrl(tenantId: string): Promise<AuthStatus> {
  return bff.get<AuthStatus>(`${PREFIX}/tenants/${tenantId}/auth-url`);
}

/** Get MP connection / token status for a tenant */
export async function getAuthStatus(tenantId: string): Promise<AuthStatus> {
  return bff.get<AuthStatus>(`${PREFIX}/tenants/${tenantId}/auth-status`);
}

/** Connect a tenant to MercadoPago and persist credentials */
export async function connectTenant(tenantId: string, input: ConnectTenantInput = {}): Promise<AuthStatus> {
  return bff.post<AuthStatus>(`${PREFIX}/tenants/${tenantId}/connect`, input);
}

/** Disconnect a tenant from MercadoPago */
export async function disconnectTenant(tenantId: string): Promise<{ message: string }> {
  return bff.post<{ message: string }>(`${PREFIX}/tenants/${tenantId}/disconnect`);
}

/** Create an order and generate a QR code for payment */
export async function createOrderAndQR(input: CreateOrderInput): Promise<QRPaymentResponse> {
  return bff.post<QRPaymentResponse>(`${PREFIX}/orders`, input);
}

/** Poll payment status for an order */
export async function getOrderStatus(orderId: string): Promise<OrderStatus> {
  return bff.get<OrderStatus>(`${PREFIX}/orders/${orderId}/status`);
}

/** Cancel a QR order */
export async function cancelQROrder(orderId: string, tenantId?: string): Promise<{ message: string }> {
  return bff.post<{ message: string }>(`${PREFIX}/orders/${orderId}/cancel`, { tenant_id: tenantId });
}

/* ─── Admin / seed ──────────────────────────────────────────────────── */

export interface SeedResult {
  registered?: boolean;
  already_registered?: boolean;
  workflow_key?: string;
  version?: number;
  error?: string;
  dev_mode?: boolean;
  message?: string;
}

/** Seed the MercadoPago workflow into the orchestrator catalog */
export async function seedWorkflowCatalog(): Promise<SeedResult> {
  return bff.post<SeedResult>(`${PREFIX}/admin/seed-workflow`);
}
